<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from "vue";
import { useCollection } from "vuefire";
import { collection, getFirestore, query, where } from "firebase/firestore";
import { useMesasStore } from "../../../services/mesas";
import { useSectoresStore } from "../../../services/sectores";
import { userUserStore } from "../../../stores/user";
import type {
  LogAcciones,
  Mesa,
  MesaUbicacion
} from "@fixeduy/restaurantestypes";
import type { Pedido } from "@fixeduy/restaurantestypes";
import { pedidoConverter } from "@fixeduy/restaurantestypes";
import ModalPedidosLiveVer from "../../PedidosLive/components/modals/ModalPedidosLiveVer.vue";
import Button from "../../../base-components/Button";
import { FormSelect } from "../../../base-components/Form";
import Lucide from "../../../base-components/Lucide/Lucide.vue";
import { getCurrentTime } from "../../../utils/fecha";
import { useLogAccionesStore } from "../../../services/logAcciones";

const props = withDefaults(defineProps<{ sectorId?: string }>(), {
  sectorId: ""
});

const mesasStore = useMesasStore();
const sectoresStore = useSectoresStore();
const userStore = userUserStore();
const logAccionesStore = useLogAccionesStore();

/** Nombre del sector para el título (General o nombre del sector). */
const sectorNombre = computed(() => {
  const s = sectoresStore.sectores.find(sec => sec.id === props.sectorId);
  return s?.nombre ?? "General";
});

// Pedidos abiertos o listos (activos en mesa) para mostrar mesaCapacidad
const pedidosActivosQuery = computed(() => {
  const empresaId = userStore.empresa?.id;
  if (!empresaId) return null;
  return query(
    collection(getFirestore(), `empresas/${empresaId}/pedidos`).withConverter(
      pedidoConverter
    ),
    where("estado", "in", ["abierto"])
  );
});
const pedidosActivos = useCollection(pedidosActivosQuery);

/** mesaId -> mesaCapacidad del pedido activo en esa mesa (el más reciente si hay varios) */
const capacidadPorMesa = computed(() => {
  const map: Record<string, number> = {};
  const list = pedidosActivos.value ?? [];
  for (const p of list) {
    const mesaId = (p as { mesaId?: string }).mesaId;
    if (mesaId != null && map[mesaId] === undefined) {
      const cap = (p as { mesaCapacidad?: number }).mesaCapacidad;
      map[mesaId] = typeof cap === "number" ? cap : 0;
    }
  }
  return map;
});

/** mesaId -> pedido activo (para "Ver pedido" al hacer click en mesa) */
const pedidoPorMesa = computed(() => {
  const map: Record<string, Pedido> = {};
  const list = pedidosActivos.value ?? [];
  for (const p of list) {
    const mesaId = (p as Pedido & { mesaId?: string }).mesaId;
    if (mesaId != null && !map[mesaId]) map[mesaId] = p as Pedido;
  }
  return map;
});

const mesaSeleccionada = ref<Mesa | null>(null);
const pedidoAVer = ref<Pedido | null>(null);

/** Zoom del mapa (1 = 100%). */
const zoomLevel = ref(1);
const zoomContainerRef = ref<HTMLElement | null>(null);

/** Solo hacer zoom con rueda cuando el dispositivo no tiene hover (ej. tablet). En computadora (mouse/trackpad) el scroll es solo desplazamiento. */
const zoomConRuedaHabilitado =
  typeof window !== "undefined" && window.matchMedia("(hover: none)").matches;

function onMapWheel(e: WheelEvent) {
  if (!editMode.value && zoomConRuedaHabilitado) {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    zoomLevel.value = Math.max(0.5, Math.min(2, zoomLevel.value + delta));
  }
}

function personasEnMesa(mesa: Mesa): number {
  const ocupada =
    (mesa.estado || "").toLowerCase() === "ocupada" ||
    (mesa.estado || "").toLowerCase() === "ocupado";
  if (!ocupada) return 0;
  const cap = capacidadPorMesa.value[mesa.id];
  if (cap !== undefined && cap > 0) return cap;
  return mesa.capacidad ?? 0;
}

const STORAGE_KEY_ASPECT = "mesas-mapa-aspect";

type TipoForma = "cuadrado" | "rectangular-vertical" | "rectangular-horizontal";

const OPCIONES_FORMAS: { id: TipoForma; valor: number; etiqueta: string }[] = [
  { id: "cuadrado", valor: 1, etiqueta: "Cuadrado" },
  {
    id: "rectangular-vertical",
    valor: 3 / 4,
    etiqueta: "Rectangular (vertical)"
  },
  {
    id: "rectangular-horizontal",
    valor: 4 / 3,
    etiqueta: "Rectangular (horizontal)"
  }
];

function valorACercanoGuardado(n: number): number {
  const dist = (a: number) => Math.abs(n - a);
  const masCercano = OPCIONES_FORMAS.reduce((prev, curr) =>
    dist(curr.valor) < dist(prev.valor) ? curr : prev
  );
  return masCercano.valor;
}

const editMode = ref(false);
const aspectRatio = ref(4 / 3);
const containerRef = ref<HTMLElement | null>(null);

// Drag state
const dragMesaId = ref<string | null>(null);
const dragStart = ref({ x: 0, y: 0, ux: 0, uy: 0 });
const draftUbicaciones = ref<Record<string, MesaUbicacion>>({});
const guardandoUbicacion = ref<string | null>(null);

// Resize state
const resizeMesaId = ref<string | null>(null);
const resizeStart = ref({ x: 0, y: 0, ancho: 10, alto: 10 });
const MIN_MESA = 5;
const MAX_MESA = 45;

const storageKeyAspect = computed(() => {
  const id = userStore.empresa?.id;
  const sector = props.sectorId ?? "";
  return id ? `${STORAGE_KEY_ASPECT}-${id}-${sector}` : null;
});

onBeforeMount(() => {
  if (mesasStore.mesas.length === 0) {
    mesasStore.getFromFirestore();
  }
  const key = storageKeyAspect.value;
  if (key) {
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const n = Number.parseFloat(raw);
        if (Number.isFinite(n) && n > 0) {
          aspectRatio.value = valorACercanoGuardado(n);
        }
      }
    } catch {
      // ignore
    }
  }
});

watch(
  () => [aspectRatio.value, storageKeyAspect.value] as const,
  ([ratio, key]) => {
    if (key && ratio) {
      try {
        localStorage.setItem(key, String(ratio));
      } catch {
        // ignore
      }
    }
  }
);

const mesasEnMapa = computed(() =>
  mesasStore.mesasConUbicacionPorSector(props.sectorId || null)
);

function ubicacionVisible(mesa: Mesa): MesaUbicacion {
  const draft = draftUbicaciones.value[mesa.id];
  if (draft) return draft;
  const u = mesa.ubicacion!;
  return {
    x: u.x,
    y: u.y,
    ancho: u.ancho ?? 10,
    alto: u.alto ?? 10
  };
}

const estiloMesa = (mesa: Mesa) => {
  const u = ubicacionVisible(mesa);
  const w = u.ancho ?? 10;
  const h = u.alto ?? 10;
  return {
    left: `${u.x}%`,
    top: `${u.y}%`,
    width: `${w}%`,
    height: `${h}%`
  };
};

const colorPorEstado = (estado: string): string => {
  const s = (estado || "").toLowerCase();
  if (s === "disponible") return "bg-emerald-500 border-emerald-600 text-white";
  if (s === "ocupada" || s === "ocupado")
    return "bg-amber-500 border-amber-600 text-white";
  if (s === "reservada") return "bg-blue-500 border-blue-600 text-white";
  return "bg-slate-400 border-slate-500 text-white";
};

/** Coordenadas de un evento mouse o touch (para soporte tablet). */
function getEventCoords(e: MouseEvent | TouchEvent): {
  clientX: number;
  clientY: number;
} {
  if ("touches" in e && e.touches.length > 0)
    return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
  if ("changedTouches" in e && e.changedTouches.length > 0)
    return {
      clientX: e.changedTouches[0].clientX,
      clientY: e.changedTouches[0].clientY
    };
  const me = e as MouseEvent;
  return { clientX: me.clientX, clientY: me.clientY };
}

function isTouchEvent(e: MouseEvent | TouchEvent): e is TouchEvent {
  return "touches" in e;
}

function startDrag(e: MouseEvent | TouchEvent, mesa: Mesa) {
  if (!editMode.value || !containerRef.value || !mesa.ubicacion) return;
  e.preventDefault();
  const rect = containerRef.value.getBoundingClientRect();
  const u = ubicacionVisible(mesa);
  const coords = getEventCoords(e);
  dragMesaId.value = mesa.id;
  dragStart.value = {
    x: coords.clientX,
    y: coords.clientY,
    ux: u.x,
    uy: u.y
  };
  draftUbicaciones.value = { ...draftUbicaciones.value, [mesa.id]: { ...u } };

  const onMove = (ev: MouseEvent | TouchEvent) => {
    if (dragMesaId.value !== mesa.id) return;
    const c = getEventCoords(ev);
    const dx = ((c.clientX - dragStart.value.x) / rect.width) * 100;
    const dy = ((c.clientY - dragStart.value.y) / rect.height) * 100;
    const ancho = u.ancho ?? 10;
    const alto = u.alto ?? 10;
    let nx = dragStart.value.ux + dx;
    let ny = dragStart.value.uy + dy;
    nx = Math.max(0, Math.min(100 - ancho, nx));
    ny = Math.max(0, Math.min(100 - alto, ny));
    draftUbicaciones.value = {
      ...draftUbicaciones.value,
      [mesa.id]: { ...u, x: nx, y: ny }
    };
  };
  const onTouchMove = (ev: TouchEvent) => {
    ev.preventDefault();
    onMove(ev);
  };
  const onUp = async () => {
    if (dragMesaId.value !== mesa.id) return;
    window.removeEventListener("mousemove", onMove as (ev: MouseEvent) => void);
    window.removeEventListener("mouseup", onUp);
    window.removeEventListener("touchmove", onTouchMove, { capture: true });
    window.removeEventListener("touchend", onUp);
    const final = draftUbicaciones.value[mesa.id];
    dragMesaId.value = null;
    if (final) {
      guardandoUbicacion.value = mesa.id;
      const ok = await mesasStore.updateUbicacion(mesa.id, final);
      guardandoUbicacion.value = null;
      if (ok) {
        const logAcciones: LogAcciones = {
          id: "",
          accion: [
            `Se modifica la ubicación de mesa ${mesa.nombre} (${mesa.id})`
          ],
          modulo: "mesas",
          fecha: getCurrentTime(),
          usuario: userStore.userLoggedIn?.uid!,
          mail: userStore.userLoggedIn?.email!
        };

        await logAccionesStore.guardarLog(logAcciones);
        const { [mesa.id]: _, ...rest } = draftUbicaciones.value;
        draftUbicaciones.value = rest;
      }
    }
  };
  if (isTouchEvent(e)) {
    window.addEventListener("touchmove", onTouchMove, {
      passive: false,
      capture: true
    });
    window.addEventListener("touchend", onUp);
  } else {
    window.addEventListener("mousemove", onMove as (ev: MouseEvent) => void);
    window.addEventListener("mouseup", onUp);
  }
}

function startResize(e: MouseEvent | TouchEvent, mesa: Mesa) {
  e.stopPropagation();
  if (!editMode.value || !containerRef.value || !mesa.ubicacion) return;
  e.preventDefault();
  const rect = containerRef.value.getBoundingClientRect();
  const u = ubicacionVisible(mesa);
  const ancho = u.ancho ?? 10;
  const alto = u.alto ?? 10;
  const coords = getEventCoords(e);
  resizeMesaId.value = mesa.id;
  resizeStart.value = { x: coords.clientX, y: coords.clientY, ancho, alto };
  draftUbicaciones.value = { ...draftUbicaciones.value, [mesa.id]: { ...u } };

  const onMove = (ev: MouseEvent | TouchEvent) => {
    if (resizeMesaId.value !== mesa.id) return;
    const c = getEventCoords(ev);
    const dx = ((c.clientX - resizeStart.value.x) / rect.width) * 100;
    const dy = ((c.clientY - resizeStart.value.y) / rect.height) * 100;
    let nw = resizeStart.value.ancho + dx;
    let nh = resizeStart.value.alto + dy;
    nw = Math.max(MIN_MESA, Math.min(MAX_MESA, nw));
    nh = Math.max(MIN_MESA, Math.min(MAX_MESA, nh));
    const current = draftUbicaciones.value[mesa.id] ?? u;
    const x = current.x ?? 0;
    const y = current.y ?? 0;
    if (x + nw > 100) nw = 100 - x;
    if (y + nh > 100) nh = 100 - y;
    nw = Math.max(MIN_MESA, nw);
    nh = Math.max(MIN_MESA, nh);
    draftUbicaciones.value = {
      ...draftUbicaciones.value,
      [mesa.id]: { ...current, ancho: nw, alto: nh }
    };
  };
  const onTouchMove = (ev: TouchEvent) => {
    ev.preventDefault();
    onMove(ev);
  };
  const onUp = async () => {
    if (resizeMesaId.value !== mesa.id) return;
    window.removeEventListener("mousemove", onMove as (ev: MouseEvent) => void);
    window.removeEventListener("mouseup", onUp);
    window.removeEventListener("touchmove", onTouchMove, { capture: true });
    window.removeEventListener("touchend", onUp);
    const final = draftUbicaciones.value[mesa.id];
    resizeMesaId.value = null;
    if (final) {
      guardandoUbicacion.value = mesa.id;
      const ok = await mesasStore.updateUbicacion(mesa.id, final);
      guardandoUbicacion.value = null;
      if (ok) {
        const logAcciones: LogAcciones = {
          id: "",
          accion: [
            `Se modifica la ubicación de mesa ${mesa.nombre} (${mesa.id})`
          ],
          modulo: "mesas",
          fecha: getCurrentTime(),
          usuario: userStore.userLoggedIn?.uid!,
          mail: userStore.userLoggedIn?.email!
        };

        await logAccionesStore.guardarLog(logAcciones);

        const { [mesa.id]: _, ...rest } = draftUbicaciones.value;
        draftUbicaciones.value = rest;
      }
    }
  };
  if (isTouchEvent(e)) {
    window.addEventListener("touchmove", onTouchMove, {
      passive: false,
      capture: true
    });
    window.addEventListener("touchend", onUp);
  } else {
    window.addEventListener("mousemove", onMove as (ev: MouseEvent) => void);
    window.addEventListener("mouseup", onUp);
  }
}
</script>

<template>
  <div class="box p-5">
    <div class="flex flex-wrap items-center justify-between gap-3 mb-3">
      <h2 class="text-base font-medium">Mapa {{ sectorNombre }}</h2>
      <span
        ><span
          class="inline-block w-3 h-3 rounded bg-emerald-500 align-middle mr-1"
        ></span>
        Disponible</span
      >
      <span
        ><span
          class="inline-block w-3 h-3 rounded bg-amber-500 align-middle mr-1"
        ></span>
        Ocupada</span
      >
      <span
        ><span
          class="inline-block w-3 h-3 rounded bg-blue-500 align-middle mr-1"
        ></span>
        Reservada</span
      >
      <span
        ><span
          class="inline-block w-3 h-3 rounded bg-slate-400 align-middle mr-1"
        ></span>
        Otro</span
      >

      <div class="flex flex-wrap items-center gap-3">
        <div class="flex items-center gap-2">
          <label
            for="forma-salon"
            class="text-xs font-medium text-slate-600 dark:text-slate-300"
            >Forma del salón:</label
          >
          <FormSelect
            id="forma-salon"
            v-model.number="aspectRatio"
            class="form-select-sm w-auto min-w-[200px] rounded border-slate-200 dark:border-darkmode-400 text-xs"
          >
            <option
              v-for="opt in OPCIONES_FORMAS"
              :key="opt.id"
              :value="opt.valor"
            >
              {{ opt.etiqueta }}
            </option>
          </FormSelect>
        </div>
        <Button
          :variant="editMode ? 'primary' : 'outline-secondary'"
          size="sm"
          @click="editMode = !editMode"
        >
          <Lucide
            v-if="!editMode"
            icon="Pencil"
            class="w-4 h-4 mr-1.5 shrink-0"
          />
          {{ editMode ? "Listo (ver)" : "Editar mapa de mesas" }}
        </Button>
      </div>
      <p v-if="editMode" class="mt-2 text-xs text-slate-500">
        Arrastrá una mesa para moverla. Usá la esquina inferior derecha para
        cambiar el tamaño. Los cambios se guardan al soltar.
      </p>
    </div>
    <!-- Zoom/pan: contenedor con scroll y zoom con rueda -->
    <div
      ref="zoomContainerRef"
      class="overflow-auto rounded-lg border border-slate-200 dark:border-darkmode-400 max-h-[70vh] min-h-[200px]"
      @wheel="onMapWheel"
    >
      <div
        class="origin-top-left"
        :style="{
          transform: `scale(${zoomLevel})`,
          width: `${100 / zoomLevel}%`,
          minHeight: '100%'
        }"
      >
        <div
          ref="containerRef"
          class="relative w-full rounded-lg overflow-hidden border-0 bg-slate-100 dark:bg-darkmode-800"
          :style="{
            aspectRatio: `${aspectRatio}`,
            touchAction: editMode ? 'none' : undefined
          }"
        >
          <!-- Grilla de fondo para referencia de alineación (cada línea cada 10%) -->
          <div
            class="absolute inset-0 pointer-events-none dark:hidden"
            style="
              background-size: 10% 10%;
              background-image:
                linear-gradient(
                  to right,
                  rgba(0, 0, 0, 0.1) 1px,
                  transparent 1px
                ),
                linear-gradient(
                  to bottom,
                  rgba(0, 0, 0, 0.1) 1px,
                  transparent 1px
                );
            "
          />
          <div
            class="absolute inset-0 pointer-events-none hidden dark:block"
            style="
              background-size: 10% 10%;
              background-image:
                linear-gradient(
                  to right,
                  rgba(255, 255, 255, 0.12) 1px,
                  transparent 1px
                ),
                linear-gradient(
                  to bottom,
                  rgba(255, 255, 255, 0.12) 1px,
                  transparent 1px
                );
            "
          />
          <div
            v-for="mesa in mesasEnMapa"
            :key="mesa.id"
            class="absolute flex flex-col items-center justify-center rounded-lg border-2 shadow min-w-[44px] min-h-[40px] p-1 text-xs font-medium select-none"
            :class="[
              colorPorEstado(mesa.estado),
              editMode && 'cursor-move hover:ring-2 hover:ring-primary/50',
              (guardandoUbicacion === mesa.id || resizeMesaId === mesa.id) &&
                'opacity-70 pointer-events-none'
            ]"
            :style="estiloMesa(mesa)"
            @mousedown="startDrag($event, mesa)"
            @touchstart.prevent="startDrag($event, mesa)"
            @click="!editMode && (mesaSeleccionada = mesa)"
          >
            <span class="font-semibold truncate max-w-full">{{
              mesa.nombre
            }}</span>
            <span v-if="!editMode" class="opacity-90">{{ mesa.estado }}</span>
            <span
              v-if="
                !editMode &&
                mesa.estado === 'ocupada' &&
                personasEnMesa(mesa) > 0
              "
              class="flex items-center justify-center gap-0.5 opacity-95"
            >
              <Lucide icon="Users" class="w-3.5 h-3.5 shrink-0" />
              <span>{{ personasEnMesa(mesa) }}</span>
            </span>
            <span
              v-else-if="
                !editMode && mesa.capacidad && mesa.estado !== 'ocupada'
              "
              class="opacity-90"
              >{{ mesa.capacidad }} pers.</span
            >
            <span
              v-if="editMode && !resizeMesaId"
              class="opacity-80 text-[10px]"
              >Arrastrar / redimensionar</span
            >
            <!-- Asa de redimensionado (esquina inferior derecha) -->
            <span
              v-if="editMode && guardandoUbicacion !== mesa.id"
              class="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize rounded-bl-lg flex items-center justify-center bg-black/20 hover:bg-black/30"
              title="Redimensionar mesa"
              @mousedown.stop="startResize($event, mesa)"
              @touchstart.stop.prevent="startResize($event, mesa)"
            >
              <svg
                class="w-3 h-3 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              >
                <path d="M15 21v-6M15 21h6M21 15v6M21 15h-6" />
              </svg>
            </span>
          </div>
          <div
            v-if="mesasEnMapa.length === 0 && !mesasStore.cargando"
            class="absolute inset-0 flex items-center justify-center text-slate-500 text-sm p-4 text-center"
          >
            No hay mesas para mostrar.
          </div>
          <div
            v-if="mesasStore.cargando"
            class="absolute inset-0 flex items-center justify-center text-slate-500 text-sm"
          >
            Cargando mesas...
          </div>
        </div>
      </div>
    </div>
    <!-- Popover mesa seleccionada (modo vista) -->
    <div
      v-if="mesaSeleccionada && !editMode"
      class="mt-3 p-3 rounded-lg border border-slate-200 dark:border-darkmode-400 bg-white dark:bg-darkmode-800 shadow-lg"
    >
      <div class="flex items-center justify-between gap-2">
        <div>
          <p class="font-semibold">{{ mesaSeleccionada.nombre }}</p>
          <p class="text-sm text-slate-500">
            Estado: {{ mesaSeleccionada.estado }}
          </p>
          <p v-if="mesaSeleccionada.capacidad" class="text-sm text-slate-500">
            Capacidad: {{ mesaSeleccionada.capacidad }} personas
          </p>
        </div>
        <div class="flex items-center gap-2">
          <Button
            v-if="pedidoPorMesa[mesaSeleccionada.id]"
            variant="primary"
            size="sm"
            @click="
              pedidoAVer = pedidoPorMesa[mesaSeleccionada.id];
              mesaSeleccionada = null;
            "
          >
            Ver pedido
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            @click="mesaSeleccionada = null"
          >
            Cerrar
          </Button>
        </div>
      </div>
    </div>
    <ModalPedidosLiveVer
      v-if="pedidoAVer"
      :pedido="pedidoAVer"
      @cerrar="pedidoAVer = null"
    />
    <div
      v-if="mesasEnMapa.length > 0 && !mesaSeleccionada"
      class="mt-3 flex flex-wrap gap-3 text-xs text-slate-500"
    ></div>
  </div>
</template>
